<script setup lang="ts">
console.log('[PartnerList] Script setup is executing!')

import { reactive, ref, computed, onMounted } from 'vue'
import PartnerFacetGroup from './PartnerFacetGroup.vue'
import PartnerResult from './PartnerResult.vue'

// =============================================================================
// PROPS
// =============================================================================

interface Props {
  apiEndpoint?: string
}

const props = withDefaults(defineProps<Props>(), {
  apiEndpoint: '/api/v2/partners/'
})

// =============================================================================
// TYPESCRIPT INTERFACES
// =============================================================================

interface Partner {
  partner: {
    name: string
    id: number
    entry: { id: number; title: string; slug: string }
    email: string | null
  }
  suites: Array<{ suite_name: string; suite_id: number; entry?: any }>
  services: Array<{ SERVICE_DISPLAY: string; id: number }>
  people: Array<{ id: number; first_name: string; last_name: string; role: string }>
}

interface Facets {
  services: Record<string, number>
  product_suites: Record<string, number>
}

interface ApiResponse {
  meta: { facets: Facets; total_count: number }
  items: Partner[]
}

// =============================================================================
// REACTIVE STATE
// =============================================================================

const state = reactive({
  partners: [] as Partner[],
  facets: { services: {}, product_suites: {} } as Facets,
  selectedServices: [] as string[],
  loading: false,
  error: null as string | null,
  hasRunSearch: false,
  keywordFilter: '',
  total_result_count: 0,
})

// =============================================================================
// DEBOUNCED SEARCH FUNCTIONALITY
// =============================================================================

let keywordSearchTimeout: ReturnType<typeof setTimeout> | null = null

function onKeywordInput() {
  if (keywordSearchTimeout) clearTimeout(keywordSearchTimeout)
  keywordSearchTimeout = setTimeout(() => {
    onKeywordSearch()
  }, 500)
}

function onKeywordSearch() {
  if (keywordSearchTimeout) {
    clearTimeout(keywordSearchTimeout)
    keywordSearchTimeout = null
  }
  state.keywordFilter = state.keywordFilter.trim()
}

// =============================================================================
// COMPUTED PROPERTIES
// =============================================================================

const hasActiveFilters = computed(() =>
  state.selectedServices.length > 0 ||
  (state.keywordFilter && state.keywordFilter.trim().length > 0)
)

const filteredPartners = computed(() =>
  state.partners.filter(partner => {
    const keywordMatch = !state.keywordFilter ||
      partner.partner.name.toLowerCase().includes(state.keywordFilter.toLowerCase())
    
    const serviceMatch = state.selectedServices.length === 0 ||
      partner.services.some(s => state.selectedServices.includes(s.SERVICE_DISPLAY))
    
    return keywordMatch && serviceMatch
  })
)

const showingResultsText = computed(() => {
  const total = filteredPartners.value.length
  if (!total) return 'No partners match your filters.'
  return `Showing ${total} result${total === 1 ? '' : 's'}`
})

// =============================================================================
// SEARCH AND FILTER FUNCTIONS
// =============================================================================

function clearAllFilters() {
  state.selectedServices = []
  state.keywordFilter = ''
}

function updateSelectedServices(services: string[]) {
  state.selectedServices = services
}

// =============================================================================
// API COMMUNICATION
// =============================================================================

async function fetchPartners() {
  console.log('[PartnerList] Fetching partners from:', props.apiEndpoint)
  state.loading = true
  state.error = null
  state.hasRunSearch = true

  try {
    const response = await fetch(props.apiEndpoint)
    console.log('[PartnerList] Response status:', response.status)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data: ApiResponse = await response.json()
    console.log('[PartnerList] Data received:', data)
    
    state.partners = data.items
    state.facets = data.meta.facets
    state.total_result_count = data.meta.total_count
  } catch (err) {
    state.error = err instanceof Error ? err.message : 'Failed to fetch partners'
    console.error('[PartnerList] Error fetching partners:', err)
  } finally {
    state.loading = false
  }
}

onMounted(() => {
  console.log('[PartnerList] Component mounted!')
  fetchPartners()
})
</script>

<template>
  <div class="flex flex-col lg:flex-row gap-8">
    <!-- Left Sidebar - Filters -->
    <aside class="lg:w-80 shrink-0">
      <div class="bg-white border border-gray-200 rounded">
        <!-- Search Box -->
        <div class="p-6 border-b border-gray-200">
          <label for="keyword-search" class="block text-sm font-semibold text-gray-900 mb-2">
            Search Partners
          </label>
          <input
            id="keyword-search"
            v-model="state.keywordFilter"
            type="search"
            placeholder="Enter keywords..."
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            @input="onKeywordInput"
          />
        </div>

        <!-- Filters -->
        <div class="p-6">
          <PartnerFacetGroup
            :facets="state.facets"
            :selected-services="state.selectedServices"
            @update:services="updateSelectedServices"
          />

          <!-- Clear Filters -->
          <div v-if="hasActiveFilters" class="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              class="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              @click="clearAllFilters"
            >
              Clear all filters
            </button>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content - Results -->
    <main class="flex-1 min-w-0">
      <!-- Loading State -->
      <div v-if="state.loading" class="text-center py-12">
        <p class="text-gray-600">Loading partners...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="state.error" class="bg-red-50 border border-red-200 rounded p-6">
        <p class="text-red-800">{{ state.error }}</p>
      </div>

      <!-- Results -->
      <div v-else>
        <!-- Results Count -->
        <div class="mb-6">
          <p class="text-sm text-gray-700">{{ showingResultsText }}</p>
        </div>

        <!-- Partner Cards -->
        <div class="space-y-4">
          <PartnerResult
            v-for="partner in filteredPartners"
            :key="partner.partner.id"
            :partner="partner"
          />
        </div>

        <!-- No Results -->
        <div v-if="filteredPartners.length === 0 && state.hasRunSearch" class="text-center py-12">
          <p class="text-gray-600">No partners found matching your criteria.</p>
        </div>
      </div>
    </main>
  </div>
</template>